import { type ComponentType, type ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePlugins } from '@/hooks/config/usePlugins';
import { useTakeoverFooterItems } from './useTakeoverFooterItems';
import { TakeoverUtilityRowView } from './TakeoverUtilityRow';

function activatePluginNavTarget(to: string) {
  window.dispatchEvent(new CustomEvent('plugin:command', { detail: to }));
}

// Rows draw their own top border; the last row shares one with the utility
// cluster beside it, so that border lives on the row wrapper instead.
const footerButtonClasses =
  'flex h-10 min-w-0 flex-1 items-center justify-start gap-2.5 rounded-none border-0 bg-transparent px-4 text-sm text-sidebar-foreground shadow-none hover:bg-sidebar-accent/30';

function FooterAction({
  to,
  label,
  icon: Icon,
  openInNewWindow,
}: {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Off-site destinations (Documentation, Releases) leave the app. */
  openInNewWindow?: boolean;
}) {
  const content: ReactNode = (
    <>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="min-w-0 truncate text-left">{label}</span>
    </>
  );
  if (to.startsWith('#')) {
    return (
      <button
        type="button"
        className={footerButtonClasses}
        data-testid={`takeover-footer-${to}`}
        onClick={() => activatePluginNavTarget(to)}
      >
        {content}
      </button>
    );
  }
  if (openInNewWindow) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noreferrer"
        className={footerButtonClasses}
        data-testid={`takeover-footer-${to}`}
        aria-label={`${label} (opens in new window)`}
      >
        {content}
        <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </a>
    );
  }
  return (
    <Link to={to} className={footerButtonClasses} data-testid={`takeover-footer-${to}`}>
      {content}
    </Link>
  );
}

/**
 * The takeover aside's pinned bottom rows: `takeover-footer` plugin nav
 * items (Send feedback, and the off-site links — Documentation, Releases —
 * that used to take rows in the account dropdown), with the plugin utility
 * icons (Chief's visor) pinned to the right of the last row. Takeover
 * surfaces hide the app sidebar and its account menu, so this is the
 * always-reachable entry for hash commands like `#feedback` and for the
 * global panels the utility icons open.
 */
export function TakeoverFeedbackFooter() {
  const items = useTakeoverFooterItems();
  const { navUtilityElements } = usePlugins();

  if (items.length === 0 && navUtilityElements.length === 0) return null;

  const leading = items.slice(0, -1);
  const last = items.at(-1);

  return (
    <div className="flex shrink-0 flex-col" data-testid="takeover-feedback-footer">
      {leading.map(({ to, label, icon, openInNewWindow }) => (
        <div key={to} className="border-t border-sidebar-border">
          <FooterAction to={to} label={label} icon={icon} openInNewWindow={openInNewWindow} />
        </div>
      ))}
      <div className="flex items-stretch border-t border-sidebar-border">
        {last && (
          <FooterAction
            to={last.to}
            label={last.label}
            icon={last.icon}
            openInNewWindow={last.openInNewWindow}
          />
        )}
        <TakeoverUtilityRowView elements={navUtilityElements} />
      </div>
    </div>
  );
}
